import React, { useState, useRef } from 'react';
import './PhotoUpload.css';

const PhotoUpload = ({ onPhotosChange, maxPhotos = 5, existingPhotos = [] }) => {
  const [photos, setPhotos] = useState(existingPhotos);
  const [error, setError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Photo labels for different views
  const photoLabels = [
    { id: 'entrance', label: 'Main Entrance / Gate', icon: '🚪' },
    { id: 'building', label: 'Building / House View', icon: '🏠' },
    { id: 'landmark', label: 'Nearby Landmark', icon: '📍' },
    { id: 'parking', label: 'Parking Area', icon: '🅿️' },
    { id: 'other', label: 'Other Helpful Photo', icon: '📷' },
  ];

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  // Process selected files
  const processFiles = (files) => {
    setError('');

    if (photos.length + files.length > maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        setError('Please select only image files');
        return;
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newPhoto = {
          id: Date.now() + Math.random(),
          src: e.target.result,
          label: photoLabels[photos.length]?.id || 'other',
          caption: '',
          file: file
        };

        setPhotos(prev => {
          const updated = [...prev, newPhoto];
          if (onPhotosChange) onPhotosChange(updated);
          return updated;
        });
      };
      reader.readAsDataURL(file);
    });
  };

  // Start camera capture
  const startCamera = async () => {
    setError('');
    try {
      // Try back camera first (for mobile), then fall back to any camera
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
      } catch {
        // Fallback to any available camera (front camera or desktop webcam)
        stream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsCapturing(true);
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access in browser settings, or use "Upload Photo" instead.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Please use "Upload Photo" to add images from your gallery.');
      } else {
        setError('Could not access camera. Please use "Upload Photo" instead.');
      }
    }
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      setError('Camera not ready. Please try again.');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Make sure video has valid dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError('Camera not ready yet. Please wait a moment and try again.');
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const imageData = canvas.toDataURL('image/jpeg', 0.8);

    const newPhoto = {
      id: Date.now() + Math.random(),
      src: imageData,
      label: photoLabels[photos.length]?.id || 'other',
      caption: '',
      file: null
    };

    setPhotos(prev => {
      const updated = [...prev, newPhoto];
      if (onPhotosChange) onPhotosChange(updated);
      return updated;
    });

    stopCamera();
    setError(''); // Clear any previous errors
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCapturing(false);
  };

  // Remove photo
  const removePhoto = (photoId) => {
    setPhotos(prev => {
      const updated = prev.filter(p => p.id !== photoId);
      if (onPhotosChange) onPhotosChange(updated);
      return updated;
    });
  };

  // Update photo caption
  const updateCaption = (photoId, caption) => {
    setPhotos(prev => {
      const updated = prev.map(p =>
        p.id === photoId ? { ...p, caption } : p
      );
      if (onPhotosChange) onPhotosChange(updated);
      return updated;
    });
  };

  // Update photo label
  const updateLabel = (photoId, label) => {
    setPhotos(prev => {
      const updated = prev.map(p =>
        p.id === photoId ? { ...p, label } : p
      );
      if (onPhotosChange) onPhotosChange(updated);
      return updated;
    });
  };

  return (
    <div className="photo-upload-container">
      <h3 className="photo-upload-title">📷 Address Photos</h3>
      <p className="photo-upload-subtitle">
        Add photos to help delivery person find your location easily
      </p>

      {error && <div className="photo-upload-error">{error}</div>}

      {/* Camera Capture View */}
      {isCapturing && (
        <div className="camera-view">
          <video ref={videoRef} autoPlay playsInline className="camera-preview" />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div className="camera-controls">
            <button className="capture-btn" onClick={capturePhoto}>
              📸 Capture
            </button>
            <button className="cancel-btn" onClick={stopCamera}>
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Photo Grid */}
      {!isCapturing && (
        <>
          <div className="photo-grid">
            {photos.map((photo, index) => (
              <div key={photo.id} className="photo-item">
                <div className="photo-preview">
                  <img src={photo.src} alt={`Location ${index + 1}`} />
                  <button
                    className="remove-photo-btn"
                    onClick={() => removePhoto(photo.id)}
                  >
                    ✕
                  </button>
                </div>
                <select
                  value={photo.label}
                  onChange={(e) => updateLabel(photo.id, e.target.value)}
                  className="photo-label-select"
                >
                  {photoLabels.map(label => (
                    <option key={label.id} value={label.id}>
                      {label.icon} {label.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Add a note (optional)"
                  value={photo.caption}
                  onChange={(e) => updateCaption(photo.id, e.target.value)}
                  className="photo-caption-input"
                />
              </div>
            ))}

            {/* Add Photo Buttons */}
            {photos.length < maxPhotos && (
              <div className="add-photo-options">
                <button
                  className="add-photo-btn camera"
                  onClick={startCamera}
                >
                  <span className="btn-icon">📷</span>
                  <span>Take Photo</span>
                </button>
                <button
                  className="add-photo-btn gallery"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className="btn-icon">🖼️</span>
                  <span>Upload Photo</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>

          {/* Photo Tips */}
          <div className="photo-tips">
            <h4>💡 Tips for good photos:</h4>
            <ul>
              <li>Take a clear photo of your main entrance or gate</li>
              <li>Include any unique landmarks nearby</li>
              <li>Show building number or name plate if visible</li>
              <li>Add notes for additional context</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoUpload;
